import { isValidYmd, MAX_SPRINT_SPAN_DAYS } from "../dateGuardrails";
import { addDaysYMD } from "../lib/date";

export type SprintGoalType = "cut" | "maintain" | "bulk";

export const SPRINT_PACE_LIMITS = {
  cutMaxLossLbsPerWeek: 2.0,
  bulkMaxGainLbsPerWeek: 1.0,
  maintainMaxAbsLbsPerWeek: 0.25,
} as const;

export function deriveSprintGoalType(startWeight: number | null | undefined, targetWeight: number | null | undefined): SprintGoalType {
  if (!Number.isFinite(startWeight) || !Number.isFinite(targetWeight)) return "maintain";
  if (Number(targetWeight) < Number(startWeight)) return "cut";
  if (Number(targetWeight) > Number(startWeight)) return "bulk";
  return "maintain";
}

export function normalizeSprintWeightsForGoalType({
  goalType,
  startWeight,
  targetWeight,
  defaultDeltaLbs = 10,
}: {
  goalType: SprintGoalType;
  startWeight: number | null;
  targetWeight: number | null;
  defaultDeltaLbs?: number;
}) {
  if (!Number.isFinite(startWeight)) return targetWeight;
  const sw = Number(startWeight);
  if (goalType === "maintain") return sw;
  if (goalType === "cut") {
    if (Number.isFinite(targetWeight) && Number(targetWeight) < sw) return Number(targetWeight);
    return sw - defaultDeltaLbs;
  }
  if (Number.isFinite(targetWeight) && Number(targetWeight) > sw) return Number(targetWeight);
  return sw + defaultDeltaLbs;
}

export function computeTargetDateFromRate({ startWeight, targetWeight, startDateYmd, rateLbsPerWeekAbs }: { startWeight: number; targetWeight: number; startDateYmd: string; rateLbsPerWeekAbs: number }) {
  const sw = typeof startWeight === "number" ? startWeight : Number(startWeight);
  const tw = typeof targetWeight === "number" ? targetWeight : Number(targetWeight);
  const rate = typeof rateLbsPerWeekAbs === "number" ? rateLbsPerWeekAbs : Number(rateLbsPerWeekAbs);
  if (!Number.isFinite(sw) || !Number.isFinite(tw) || !Number.isFinite(rate) || rate <= 0) return null;
  if (!isValidYmd(startDateYmd)) return null;
  const diff = Math.abs(tw - sw);
  if (diff < 0.001) return null;
  const weeksNeeded = diff / rate;
  let daysNeeded = Math.ceil(weeksNeeded * 7);
  let clamped = false;
  if (daysNeeded < 7) { daysNeeded = 7; clamped = true; }
  if (daysNeeded > MAX_SPRINT_SPAN_DAYS) { daysNeeded = MAX_SPRINT_SPAN_DAYS; clamped = true; }
  const targetDateYmd = addDaysYMD(startDateYmd, daysNeeded);
  return { targetDateYmd, clamped };
}

export function getRatePresetOptions({ startWeight, targetWeight }: { startWeight: number; targetWeight: number }) {
  const sw = typeof startWeight === "number" ? startWeight : Number(startWeight);
  const tw = typeof targetWeight === "number" ? targetWeight : Number(targetWeight);
  if (!Number.isFinite(sw) || !Number.isFinite(tw)) return [];
  if (Math.abs(tw - sw) < 0.001) {
    return [{ label: "Maintain (0.0 lb/week)", rate: 0, disabled: true }];
  }
  if (tw < sw) {
    return [
      { label: "Lean", rate: 0.5 },
      { label: "Moderate", rate: 1.0 },
      { label: "Aggressive", rate: 1.5 },
      { label: "Extreme", rate: 2.0 },
    ];
  }
  return [
    { label: "Lean", rate: 0.25 },
    { label: "Moderate", rate: 0.5 },
    { label: "Aggressive", rate: 1.0 },
  ];
}

export function getLoggedWeightForDate(entries: Array<{ date?: string; weight?: number | null }>, dateYmd: string): number | null {
  if (!Array.isArray(entries) || !isValidYmd(dateYmd)) return null;
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const entry = entries[i];
    if (entry?.date !== dateYmd) continue;
    if (Number.isFinite(entry.weight)) return Number(entry.weight);
  }
  return null;
}

export function computeSprintPlanMetrics({ startWeight, targetWeight, startDate, targetDate }: { startWeight: number | null; targetWeight: number | null; startDate: string; targetDate: string }) {
  const durationDays = isValidYmd(startDate) && isValidYmd(targetDate)
    ? Math.floor((new Date(targetDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const deltaLbs = Number.isFinite(startWeight) && Number.isFinite(targetWeight) ? Number(targetWeight) - Number(startWeight) : null;
  const paceLbsPerWeek = deltaLbs !== null && durationDays !== null && durationDays > 0
    ? (deltaLbs / durationDays) * 7
    : null;
  return { durationDays, deltaLbs, paceLbsPerWeek };
}

export function validateSprintGuardrails({ startWeight, targetWeight, startDate, targetDate, goalType = deriveSprintGoalType(startWeight, targetWeight) }: { startWeight: number | null; targetWeight: number | null; startDate: string; targetDate: string; goalType?: SprintGoalType }) {
  const hardErrors: string[] = [];
  const softWarnings: string[] = [];
  const { durationDays, deltaLbs, paceLbsPerWeek } = computeSprintPlanMetrics({ startWeight, targetWeight, startDate, targetDate });

  if (!isValidYmd(startDate) || !isValidYmd(targetDate) || targetDate <= startDate) {
    hardErrors.push("End date must be after start date.");
  }

  if (durationDays !== null) {
    if (durationDays < 7) hardErrors.push("Sprint duration must be at least 7 days.");
    if (durationDays > 365) hardErrors.push("Sprint duration cannot exceed 365 days.");
    if (durationDays > 180) softWarnings.push("Very long sprint.");
  }

  if (Number.isFinite(startWeight) && Number.isFinite(targetWeight) && Number(startWeight) > 0) {
    const absDelta = Math.abs(Number(targetWeight) - Number(startWeight));
    if (absDelta > Number(startWeight) * 0.25) {
      hardErrors.push("Total change cannot exceed 25% of starting weight.");
    }
    const delta = Number(targetWeight) - Number(startWeight);
    if (goalType === "cut" && delta >= -0.0001) {
      hardErrors.push("Cut mode requires target weight below starting weight.");
    }
    if (goalType === "maintain" && Math.abs(delta) > 0.0001) {
      hardErrors.push("Maintain mode requires target weight equal to starting weight.");
    }
    if (goalType === "bulk" && delta <= 0.0001) {
      hardErrors.push("Bulk mode requires target weight above starting weight.");
    }
  }

  if (Number.isFinite(paceLbsPerWeek)) {
    const pace = Number(paceLbsPerWeek);
    if (pace < -SPRINT_PACE_LIMITS.cutMaxLossLbsPerWeek) hardErrors.push("Weight loss pace cannot exceed 2.0 lb/week.");
    if (pace > SPRINT_PACE_LIMITS.bulkMaxGainLbsPerWeek) hardErrors.push("Weight gain pace cannot exceed 1.0 lb/week.");
    if (pace < -1.5) softWarnings.push("Aggressive cut.");
    if (pace > 1.0) softWarnings.push("Aggressive bulk.");
  }

  return { hardErrors, softWarnings, durationDays, deltaLbs, paceLbsPerWeek };
}
